import GitInfo from 'react-git-info/macro';
import api from 'services/api';
import {
  workspaceSelectedForWorking,
  setUrlReturn
} from 'Screens/Contextualization/Faraday/actions/Actions';
import { getWorkspaces, resetError as resetWorkspaceError } from 'Screens/Contextualization/Workspaces/actions/Actions';
import Cookies from 'universal-cookie';
import includes from 'lodash/includes';

import { SAVE_PREFERENCES_SUCCESS, updatePreferences } from 'store/Preferences/actions';
import { trackEvent } from 'tracking/GA';
import mappedPermissions from 'Hooks/usePermission/mappedPermissions';
import { saveConfig } from 'store/Config/actions';
import { ACTIONS, CATEGORIES } from 'tracking/GA/constants';
import { redirect } from 'store/Router/actions';
import { selectIsLoggedIn, selectRememberMe, selectUserRoles, selectRequest2fa } from 'store/Sesion/selectors';
import { selectActiveWorkspaces } from 'store/Workspace/selectors';
import { selectLocation, selectPathname } from 'store/Router/selectors';
import { selectUrlReturn } from 'store/Faraday/selectors';
import { setError, hideError } from 'store/errors/actions';
import { removeUnfinishedCommands, showProcessingQueue } from 'Common/Components/FileUploadContext/actions';
import { selectIsSsoEnabled } from 'store/Config/selector';

export const LOGIN_START = 'LOGIN_START';
export const LOGIN_SUCCESS = 'LOGIN_SUCCESS';
export const LOGIN_FAIL = 'LOGIN_FAIL';
export const LOGIN_FETCH_FINISHED = 'LOGIN_FETCH_FINISHED';
export const LOGOUT_SUCCESS = 'LOGOUT_SUCCESS';
export const HIDE_ERROR_MESSAGE_LOGIN = 'HIDE_ERROR_MESSAGE_LOGIN';
export const LOGOUT_SUCCESS_REDIRECT = 'LOGOUTSUCCESS_REDIRECT';
export const NOT_LOGGED = 'NOT_LOGGED';

export const REQUEST_SECOND_FACTOR_LOGIN = 'REQUEST_SECOND_FACTOR_LOGIN';
export const LOAD_PREFERENCES = 'LOAD_PREFERENCES';
export const SET_FIRST_LOGIN = 'SET_FIRST_LOGIN';
export const SET_REMEMBER_ME = 'SET_REMEMBER_ME';

export const FORGOT_PASSWORD_REQUEST = 'FORGOT_PASSWORD_REQUEST';
export const FORGOT_PASSWORD_SUCCESS = 'FORGOT_PASSWORD_SUCCESS';
export const FORGOT_PASSWORD_FAILURE = 'FORGOT_PASSWORD_FAILURE';

export const RESET_PASSWORD_REQUEST = 'RESET_PASSWORD_REQUEST';
export const RESET_PASSWORD_SUCCESS = 'RESET_PASSWORD_SUCCESS';
export const RESET_PASSWORD_FAILURE = 'RESET_PASSWORD_FAILURE';

export const LOGIN_RESET = 'LOGIN_RESET';
export const LOGIN_2FA_START = 'LOGIN_2FA_START';
export const VALIDATING_SESSION_START = 'VALIDATING_SESSION_START';
export const VALIDATING_SESSION_END = 'VALIDATING_SESSION_END';
export const SAVE_USER = 'SAVE_USER';

function failureLoginCallback () {
  return (dispatch) => {
    dispatch({ type: LOGIN_FAIL, data: { message: 'An error has occurred.' } });
  };
}

export function failureSessionValidationCallback () {
  return (dispatch) => {
    dispatch({ type: NOT_LOGGED });
  };
}

export function setUser (user, roles, csrfToken) {
  return (dispatch) => {
    dispatch({
      type: LOGIN_SUCCESS, user, roles, csrfToken
    });
  };
}

export function getApiConfig () {
  return async (dispatch) => {
    let config = {};
    try {
      config = await api.faraday.fetchApiConfig();

      if (config.client_key === undefined) config.client_key = 'white';

      dispatch(saveConfig(config));
    } catch (e) {
      dispatch(setError('There was an error while fetching config'));
    }
    return config;
  };
}

function successSessionCallback (data) {
  return async (dispatch) => {
    dispatch(hideError());
    dispatch(resetWorkspaceError());
    const gitInfo = GitInfo();
    const config = await dispatch(getApiConfig());

    await dispatch({
      type: LOAD_PREFERENCES,
      permissions: mappedPermissions(data.permissions),
      hash: gitInfo.commit.shortHash,
      role: data.roles[0],
      edate: config.edate,
      email: config.email,
      expired: config.expired,
      wsPort: config.websocket_port,
      notificationsEnabled: config.notifications_enabled,
      licenseType: config.type,
      userName: data.name,
      user_id: data.user_id,
      featureFlags: data.feature_flags
    });

    await dispatch({ type: SAVE_PREFERENCES_SUCCESS, preferences: data.preferences });

    await dispatch(setUser(data.name, data.roles, data.csrf_token));

    // obtengo workspaces, esto se usa en todos lados.
    await dispatch(getWorkspaces(false, false, true));

    const savedThemeInBrowser = localStorage.getItem('theme');
    if (!data?.preferences?.theme && !savedThemeInBrowser) dispatch(updatePreferences({ theme: 'light' }));
    if (!data?.preferences?.theme && savedThemeInBrowser) dispatch(updatePreferences({ theme: savedThemeInBrowser }));
  };
}

export function createSession () {
  return async (dispatch) => {
    try {
      const data = await api.faraday.getSession();
      await dispatch(successSessionCallback(data));
    } catch (e) {
      await dispatch(failureSessionValidationCallback());
    }
  };
}

function twoFaSuccess (response, user) {
  return (dispatch) => {
    dispatch({ type: SAVE_USER, user });
    if (response.code === 202) {
      dispatch({ type: REQUEST_SECOND_FACTOR_LOGIN });
    } else {
      dispatch(createSession());
    }
  };
}

export function setFirstLogin (workspace = 'faraday_beta') {
  return async (dispatch) => {
    await dispatch({ type: SET_FIRST_LOGIN, firstLogin: true });
    await dispatch(workspaceSelectedForWorking(workspace));
    dispatch(redirect('/workspaces'));
  };
}

export function loginRedirect () {
  return async (dispatch, getState) => {
    const workspaces = selectActiveWorkspaces(getState()); // listado de todos los workspaces
    const roles = selectUserRoles(getState()); // roles del usuario
    const urlReturn = selectUrlReturn(getState());
    const pathname = selectPathname(getState());
    const ssoEnabled = selectIsSsoEnabled(getState());
    const request2fa = selectRequest2fa(getState());

    if (urlReturn &&
        !['/login', '/logout', '/'].includes(urlReturn) &&
        !urlReturn.startsWith('/resetpass') && urlReturn !== '/' && !request2fa) {
      dispatch(redirect(urlReturn));
      dispatch(setUrlReturn(''));
    } else if (includes(roles, 'admin') && workspaces.length === 0) {
      dispatch(redirect('/workspaces'));
    } else if (((pathname === '/') && ssoEnabled) && !request2fa) {
      dispatch(redirect('/workspaces'));
    } else {
      dispatch(redirect('/workspaces'));
    }
  };
}

export function validateSession () {
  return async (dispatch, getState) => {
    const location = selectLocation(getState());
    let isLoggedIn = selectIsLoggedIn(getState());
    dispatch({ type: VALIDATING_SESSION_START });

    if (!isLoggedIn && (
      !includes(['/logout', '/forgotpass'], location.pathname) &&
        !location.pathname.startsWith('/resetpass'))
    ) {
      await dispatch(createSession()); // valido y creo la sesion usando la cookie del browser.
    }
    dispatch({ type: VALIDATING_SESSION_END });

    isLoggedIn = selectIsLoggedIn(getState());

    if (isLoggedIn && includes(['/login', '/'], location.pathname)) {
      dispatch(loginRedirect()); // si esta logueado lo redirecciono a donde corresponde
    } else if (!isLoggedIn && (
      !includes(['/logout', '/forgotpass'], location.pathname) &&
        !location.pathname.startsWith('/resetpass'))) {
      dispatch(redirect('/login')); // si no esta logueado va al login
    }
    // por default queda donde está
  };
}

export function loginStart () {
  return (dispatch) => {
    dispatch({ type: LOGIN_START });
  };
}

export function login (user, password) {
  return async (dispatch, getState) => {
    try {
      const remember = selectRememberMe(getState());

      dispatch(trackEvent(CATEGORIES.account, ACTIONS.logIn.name, ACTIONS.logIn.label));

      const response = await api.auth.login({ email: user || '', password: password || '', remember });

      const responseCode = response?.code ?? response?.meta?.code;

      if (responseCode === 202) {
        dispatch({ type: SAVE_USER, user });
        dispatch({ type: REQUEST_SECOND_FACTOR_LOGIN });
        dispatch(loginRedirect());
      }
      // reconstruye sesion
      await dispatch(createSession());

      // todo ok => redirecciona a donde corresponda
      if (responseCode === 200) {
        dispatch(loginRedirect());
      }

      // Force loading spinner of the Login button to change after the redirect
      dispatch({ type: LOGIN_FETCH_FINISHED });
    } catch (e) {
      dispatch(failureLoginCallback());
    }
  };
}

// TODO: Must comment line 196 in app.py of faraday
export function logOut () {
  return async (dispatch) => {
    try {
      const cookies = new Cookies();
      cookies.remove('remember_token');
      await api.auth.logout();
      dispatch({ type: LOGOUT_SUCCESS });
      dispatch(removeUnfinishedCommands());
      dispatch(showProcessingQueue(false));
    } catch (e) {
      dispatch({ type: LOGOUT_SUCCESS_REDIRECT });
    }
    dispatch(setUrlReturn(''));
    dispatch(hideError());
    dispatch(resetWorkspaceError());
    dispatch(redirect('/login', false));
  };
}

export function hideErrorMessage () {
  return (dispatch) => {
    dispatch({ type: HIDE_ERROR_MESSAGE_LOGIN });
  };
}

function twoFaFail () {
  return (dispatch) => {
    dispatch({ type: LOGIN_FAIL, data: { message: 'Invalid token.' } });
  };
}

export function login2fa (token, user) {
  return async (dispatch) => {
    dispatch({ type: LOGIN_2FA_START });
    try {
      const response = await api.auth.twoFaConfirm({ secret: token });
      await dispatch(twoFaSuccess(response, user));

      dispatch({ type: LOGIN_FETCH_FINISHED });

      // reconstruye sesion
      await dispatch(createSession());

      // todo ok => redirecciona a donde corresponda
      dispatch(loginRedirect());
    } catch (e) {
      dispatch(twoFaFail());
    }
  };
}

export function setRememberMe (value) {
  return (dispatch) => {
    dispatch({ type: SET_REMEMBER_ME, value });
  };
}

export function forgotPassword (email) {
  return async (dispatch) => {
    dispatch({ type: FORGOT_PASSWORD_REQUEST });
    try {
      await api.faraday.forgotPassword(email);
      return dispatch({ type: FORGOT_PASSWORD_SUCCESS, email });
    } catch (e) {
      dispatch({ type: FORGOT_PASSWORD_FAILURE, data: { message: 'An error has occurred.' } });
      return dispatch(setError(e.response.message));
    }
  };
}

export function resetPassword (hash, password, repeatPassword) {
  return async (dispatch) => {
    dispatch({ type: RESET_PASSWORD_REQUEST });
    try {
      await api.faraday.resetPassword(hash, password, repeatPassword);
      return dispatch({ type: RESET_PASSWORD_SUCCESS });
    } catch (e) {
      return dispatch({ type: RESET_PASSWORD_FAILURE, data: { message: 'An error has occurred.' } });
    }
  };
}

export function resetLogin () {
  return async (dispatch) => {
    dispatch({ type: LOGIN_RESET });
  };
}
